import { supabase } from '../config/supabase';
import { ownerService } from './ownerService';
import { hireCodeRatioSettingsService, HireCodeRatioConfig } from './hireCodeRatioSettingsService';

/**
 * Team Member Hire to Code Ratio Metrics Service
 * 
 * This service handles all metrics calculations for team members including:
 * - Hire per Code Ratio (Last 2 Months & All Time)
 * - Jotform to Hire Ratio (Last 2 Months & All Time)
 */

export interface TeamMemberHireCodeMetrics {
  enrolled: number;
  waiting: number;
  ratio: number | null;
  currentEnrolled: number;
  currentWaiting: number;
  currentRatio: number | null;
  jotformEnrolled: number;
  jotformInvitations: number;
  jotformRatio: number | null;
  jotformCurrentEnrolled: number;
  jotformCurrentInvitations: number;
  jotformCurrentRatio: number | null;
}

/**
 * Get team member hire per code ratio (all time - includes archived)
 * @param teamMemberEmail - The email of the team member
 * @returns TeamMemberHireCodeMetrics object with all calculated metrics
 */
export async function getTeamMemberHireRatio(
  teamMemberEmail: string
): Promise<TeamMemberHireCodeMetrics> {
  try {
    const orgId = await ownerService.getOwnerOrgId();
    if (!orgId) {
      return getEmptyMetrics();
    }

    const ratioConfig: HireCodeRatioConfig = await hireCodeRatioSettingsService.getSettings(Number(orgId));

    const emailPattern = `%${teamMemberEmail}%`;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 60);
    const cutoffIso = cutoffDate.toISOString();

    const getCount = async (
      table: 'new_hire' | 'new_hire_archive',
      {
        progressValues,
        createdAfter,
      }: {
        progressValues: number[];
        createdAfter?: string;
      },
    ): Promise<number> => {
      if (progressValues.length === 0) return 0;

      let query = supabase
        .from(table)
        .select('*', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .ilike('recruiter_emails', emailPattern)
        .in('progress', progressValues);

      if (createdAfter) {
        query = query.gte('created_at', createdAfter);
      }

      const { count, error } = await query;
      if (error) {
        console.error(`Error counting ${table}:`, error);
        throw error;
      }
      return count ?? 0;
    };

    const [
      recentEnrolledActive,
      recentEnrolledArchived,
      allEnrolledActive,
      allEnrolledArchived,
      recentWaitingActive,
      recentWaitingArchived,
      allWaitingActive,
      allWaitingArchived,
      recentInvitationsActive,
      recentInvitationsArchived,
      allInvitationsActive,
      allInvitationsArchived,
      recentJotformHiresActive,
      recentJotformHiresArchived,
      allJotformHiresActive,
      allJotformHiresArchived,
    ] = await Promise.all([
      // LAST 2 MONTHS HIRE PER CODE RATIO - [HIRE]
      getCount('new_hire', { progressValues: ratioConfig.hire_stages, createdAfter: cutoffIso }),
      getCount('new_hire_archive', { progressValues: ratioConfig.hire_stages, createdAfter: cutoffIso }),

      // ALL TIME HIRE PER CODE RATIO - [HIRE]
      getCount('new_hire', { progressValues: ratioConfig.hire_stages }),
      getCount('new_hire_archive', { progressValues: ratioConfig.hire_stages, createdAfter: cutoffIso }),

      // LAST 2 MONTHS HIRE PER CODE RATIO - [CODE]
      getCount('new_hire', { progressValues: ratioConfig.coded_stages, createdAfter: cutoffIso }),
      getCount('new_hire_archive', { progressValues: ratioConfig.coded_stages, createdAfter: cutoffIso }),

      // ALL TIME HIRE PER CODE RATIO - [CODE]
      getCount('new_hire', { progressValues: ratioConfig.coded_stages }),
      getCount('new_hire_archive', { progressValues: ratioConfig.coded_stages }),

      // LAST 2 MONTHS JOTFORM TO HIRE RATIO - [INVITATION SENT]
      getCount('new_hire', { progressValues: ratioConfig.jotform_invitation, createdAfter: cutoffIso }),
      getCount('new_hire_archive', { progressValues: ratioConfig.jotform_invitation, createdAfter: cutoffIso }),

      // ALL TIME JOTFORM TO HIRE RATIO - [INVITATION SENT]
      getCount('new_hire', { progressValues: ratioConfig.jotform_invitation }),
      getCount('new_hire_archive', { progressValues: ratioConfig.jotform_invitation }),

      // LAST 2 MONTHS JOTFORM TO HIRE RATIO - [HIRE]
      getCount('new_hire', { progressValues: ratioConfig.jotform_enrolled, createdAfter: cutoffIso }),
      getCount('new_hire_archive', { progressValues: ratioConfig.jotform_enrolled, createdAfter: cutoffIso }),

      // ALL TIME JOTFORM TO HIRE RATIO - [HIRE]
      getCount('new_hire', { progressValues: ratioConfig.jotform_enrolled }),
      getCount('new_hire_archive', { progressValues: ratioConfig.jotform_enrolled }),
    ]);

    const currentEnrolled = recentEnrolledActive + recentEnrolledArchived;
    const currentWaiting = recentWaitingActive + recentWaitingArchived;
    const currentRatio = currentEnrolled === 0 ? null : currentWaiting / currentEnrolled;

    const enrolled = allEnrolledActive + allEnrolledArchived;
    const waiting = allWaitingActive + allWaitingArchived;
    const ratio = enrolled === 0 ? null : waiting / enrolled;

    const jotformCurrentInvitations = recentInvitationsActive + recentInvitationsArchived;
    const jotformCurrentEnrolled = recentJotformHiresActive + recentJotformHiresArchived;
    const jotformCurrentRatio = jotformCurrentInvitations === 0 ? null : jotformCurrentEnrolled / jotformCurrentInvitations;

    const jotformInvitations = allInvitationsActive + allInvitationsArchived;
    const jotformEnrolled = allJotformHiresActive + allJotformHiresArchived;
    const jotformRatio = jotformInvitations === 0 ? null : jotformEnrolled / jotformInvitations;

    console.log(`[getTeamMemberHireRatio] Metrics for ${teamMemberEmail}:`, {
      orgId,
      emailPattern,
      ratioConfig,
      'ALL TIME Jotform Enrolled': {
        new_hire: allJotformHiresActive,
        new_hire_archive: allJotformHiresArchived,
        total: jotformEnrolled,
      },
      'ALL TIME Jotform Invitations': {
        new_hire: allInvitationsActive,
        new_hire_archive: allInvitationsArchived,
        total: jotformInvitations,
      },
      'LAST 2 MONTHS Jotform Enrolled': {
        new_hire: recentJotformHiresActive,
        new_hire_archive: recentJotformHiresArchived,
        total: jotformCurrentEnrolled,
      },
    });

    return {
      enrolled,
      waiting,
      ratio,
      currentEnrolled,
      currentWaiting,
      currentRatio,
      jotformEnrolled,
      jotformInvitations,
      jotformRatio,
      jotformCurrentEnrolled,
      jotformCurrentInvitations,
      jotformCurrentRatio,
    };
  } catch (error) {
    console.error('Error fetching team member hire ratio:', error);
    return getEmptyMetrics();
  }
}

/**
 * Returns empty metrics object with all zeros and null ratios
 */
function getEmptyMetrics(): TeamMemberHireCodeMetrics {
  return {
    enrolled: 0,
    waiting: 0,
    ratio: null,
    currentEnrolled: 0,
    currentWaiting: 0,
    currentRatio: null,
    jotformEnrolled: 0,
    jotformInvitations: 0,
    jotformRatio: null,
    jotformCurrentEnrolled: 0,
    jotformCurrentInvitations: 0,
    jotformCurrentRatio: null,
  };
}

